import { tPipeContext } from './Pipe.types';

export const BASE_URL_PIPE_LEGACY = import.meta.env
  .VITE_BASE_URL_PIPE_LEGACY as string;

export const initialState: tPipeContext = {
  currentFunnel: null,
  loading: false,
  editMode: false,
  selectedStage: null,
  deleteStageModalIsOpen: false,
  autoAssignModalIsOpen: false,
  stuckLeadsModalIsOpen: false,
  bulkMessageWABModalIsOpen: false,
  deleteFunnelModalIsOpen: false,
  loadingStagesForDeleteFunnelModal: false,
  funnelsWithStages: null,
  workspaceFunnels: [],
  createNewFunnelMode: false,
  loadingFilteredLeads: false,
  isUndeliveredEnabled: false,
  isUndeliveredFilterOn: false,
  undeliveredCount: null,
  areStageLeadCountsLoaded: false,
  expiredLeadIds: new Set<string>(),
  minuteKey: 0,
  boardExpiredLead: null,
  // Inlined rather than importing EMPTY_SELECTION: Pipe.functions already
  // imports this module, so importing back would create a cycle.
  selection: { stageId: null, leadIds: new Set<string>(), anchorId: null },
  dispatchSelection: () => {},
  applyBulkSelectionResult: () => {},
  onDragEnd: async () => {},
  addStageInIndex: async () => {},
  removeStage: async () => {},
  updateFunnelName: () => {},
  updateFunnelDescription: () => {},
  updateFunnelEmoji: () => {},
  toggleEditMode: () => {},
  updateFunnelOnEditMode: async () => {},
  setSelectedStage: () => {},
  setDeleteStageModalIsOpen: () => {},
  setAutoAssignModalIsOpen: () => {},
  updateAutoAssignUserStatus: async () => {},
  setStuckLeadsModalIsOpen: () => {},
  updateExpiresAfterValue: async () => ({ ok: false }),
  updateStageName: async () => {},
  setDeleteFunnelModalIsOpen: () => {},
  removeFunnel: async () => {},
  createNewFunnel: async () => {},
  setFunnelsWithStages: () => {},
  updateFunnel: async () => {},
  setWorkspaceFunnels: () => {},
  setCreateNewFunnelMode: () => {},
  getFunnelsWithStages: async () => {},
  getMoreLeads: async () => null,
  createNewLead: async () => {},
  getSelectedFunnelInfo: async () => {},
  setBulkMessageWABModalIsOpen: () => {},
  fetchOrGetTemplates: async () => {},
  getSpecificLeads: async () => null,
  getSpecificLeadsWithData: async () => null,
  createCampaignStage: async () => {},
  createCampaignLeads: async () => {},
  getTemplates: async () => ({ templates: [], predefinedVariables: undefined }),
  handleUpgradeOrIncreaseLimit: () => {},
  updateStageConfiguration: async () => {},
  updateLeadAiRespondState: () => {},
  moveLeadToStage: () => {},
  removeLeadFromFunnel: () => {},
  updateLeadAssignee: () => {},
  onLeadTagsChanged: () => {},
  stageDescriptionModalIsOpen: false,
  setStageDescriptionModalIsOpen: () => {},
  createStageModalIsOpen: false,
  setCreateStageModalIsOpen: () => {},
  createStageAtIndex: null,
  setCreateStageAtIndex: () => {},
  slbWorkspaceEnabled: false,
  setSlbWorkspaceEnabled: () => {},
  slbHasActiveIntegration: false,
  slbConfiguredFunnelId: null,
  slbActiveFunnelIds: [],
  slbEscalatedLeadIds: new Set<string>(),
  slbAcknowledgedLeadIds: new Set<string>(),
  acknowledgeSlbEscalatedLead: () => {},
  slbEngagementByLead: new Map(),
  slbLeadChatMap: new Map<string, { chatId: string; channelType: string }>(),
  cardViewMode: 'compact',
  setCardViewMode: () => {},
};

export { integrationIcons } from 'src/constants/channel.constants';

export const initialStagesForCreatingFunnel = [
  { name: 'Prospectos' },
  { name: 'Asignados' },
  { name: 'Contactados' },
  { name: 'Cierre' },
];

export const initialValuesForCreateFunnelForm = {
  name: '',
  description: '',
};

/**
 * KTD10 — the transitional unit marker on the funnel write contract.
 *
 * `stages.expires_after` changes meaning at the migration (hours before,
 * minutes after) and a browser tab from before the release keeps resubmitting
 * every stage's threshold from memory for days. The backend tells the two
 * bundles apart by the presence of this field, so `PipeService.updateFunnel` —
 * the single request builder behind the funnel PUT — attaches it to every
 * write. Retired once old bundles are gone.
 */
export const EXPIRES_AFTER_UNIT = 'minutes';

export const INBOX_STAGE_NAME = 'Inbox 📥';

export const UNCLASSIFIED_UUID = 'unclassified';

/**
 * localStorage key the Pipe page reads (via ahooks useLocalStorageState) to
 * decide which funnel to display on mount. Exported so cross-page deep-links —
 * e.g. the Lead Agent funnel-move note (RET-1311) — can seed the destination
 * funnel before navigating here. ahooks JSON-serializes the value, so external
 * writers MUST JSON.stringify the funnel id.
 */
export const CURRENT_FUNNEL_ID_STORAGE_KEY = 'currentFunnelID';

export const lastMessageOnFormat = 'DD/M/YYYY, HH:mm:ss';
export const ISO601Format = 'YYYY-MM-DDTHH:mm:ss.SSSSSS';

/**
 * Largest selection a single synchronous bulk action will submit.
 *
 * Sized against the binding client ceiling: `axiosInstance` in
 * `src/axios/axios.singleton.ts` sets `timeout: 30_000` (BUG-262).
 *
 * MEASURED 2026-08-16 on staging, against the set-based write path, from the
 * network HAR of two recorded sessions (`timings.wait` == total, so these are
 * server-side figures with no network noise):
 *
 *   | leads | composition                    | chunks | wall   |
 *   |-------|--------------------------------|--------|--------|
 *   | 300   | all unassigned (per-model)     | 1      | 6.31 s |
 *   | 300   | all unassigned, empty pool     | 1      | 6.56 s |
 *   | 550   | 300 unassigned + 250 assigned  | 2      | 6.59 s |
 *   | 575   | 251 unassigned + 324 assigned  | 2      | 6.87 s |
 *
 * Per-lead cost is NOT uniform — it depends on the assigned/unassigned mix, not
 * on the size:
 *
 *   - assigned   -> set-based path,  ~0.12 ms/lead
 *   - unassigned -> per-model save,  ~21 ms/lead  (kept deliberately: it is what
 *                   preserves RET-1207 auto-assignment)
 *
 * So the worst case is a stage that is 100% unassigned — common, and the one
 * that binds. At ~21 ms/lead the 30 s ceiling breaks at roughly 1,370 leads.
 * This cap sits below that with margin.
 *
 * Three separate bounds exist and they are NOT the same number — do not
 * conflate them:
 *
 *   - this cap (1000)      — largest selection the client will submit at all
 *   - LEADS_BULK_ASYNC_THRESHOLD — above it the server answers 202 and resolves
 *                            asynchronously. Code default 500; currently
 *                            deployed at 0, i.e. async disabled, until its
 *                            websocket bindings and 202 handling land.
 *   - the confirmation threshold — above it the user types the target count to
 *                            proceed. Not implemented yet.
 *
 * They serve different purposes, so they need to be coherent, not equal. With
 * async enabled at 500, a 501-1000 selection would resolve asynchronously and a
 * smaller one synchronously — which is the intended shape. Today, with async
 * off, everything up to this cap runs synchronously: 1000 unassigned leads is
 * ~21 s against a 30 s ceiling, which is inside the budget but not by much.
 * That is the reason this cap is not higher.
 */
export const SELECTION_SUBMIT_CAP = 1000;

/**
 * KTD5 — the board re-derives each card's red state on this cadence. Sixty
 * seconds because `vw_leads` evaluates the threshold at minute resolution
 * (`date_trunc('minute', …)`), so a faster tick could only ever anticipate the
 * server's own answer.
 */
export const MINUTE_IN_MS = 60_000;
export const STAGNANT_TICK_INTERVAL_MS = MINUTE_IN_MS;

// Fired on `window` after a lead is dropped into another stage and the API
// confirms it. The Centro de Retos "Mueve un lead" tour advances on it
// (Tour `advanceOn`), so a real drag ends the tour the way "Listo" would.
export const LEAD_MOVED_EVENT = 'leadsales:lead-moved';

/**
 * INT-1591: the funnel board's undelivered mark ships with the chat list's
 * flag — the same feature, rolled out to the same cohort (D6). One constant,
 * so the board can move to a flag of its own without touching its call sites.
 */
export const UNDELIVERED_FEATURE_FLAG = 'chat_undelivered-list' as const;

export const ENABLE_FUNNEL_BULK_MOVE = 'leads_funnel-bulk-move';
